import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import {
  createProductTag,
  updateProductTag,
  previewProductTagScan,
  enqueueProductTagScan,
} from "@/api/productTags.js";
import { getAdminAvailableCategories } from "@/api/masterMenu.js";
import { normalizeAvailableCategories } from "@/pages/products/masterMenuPublish.js";
import CategoryMultiSelect from "./CategoryMultiSelect.jsx";

const PRESET_COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#f43f5e",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#64748b",
  "#111827",
];

const DEFAULT_COLOR = PRESET_COLORS[0];

function ProductTagModal({ isOpen, setIsOpen, tag, onSave, onRequestScan }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [id, setId] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [ordinal, setOrdinal] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [matchingPrompt, setMatchingPrompt] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const isEditMode = Boolean(tag?.id);
  const originalPrompt = (tag?.product_matching_prompt || "").trim();

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setCategoriesLoading(true);
    getAdminAvailableCategories()
      .then((data) => {
        if (!cancelled) setCategoryOptions(normalizeAvailableCategories(data));
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setId(tag?.id || "");
    setIsPublic(Boolean(tag?.public));
    setDisplayValue(tag?.display?.value || "");
    setOrdinal(
      tag?.display?.ordinal === undefined || tag?.display?.ordinal === null
        ? ""
        : String(tag.display.ordinal)
    );
    setColor(tag?.display?.color || DEFAULT_COLOR);
    setMatchingPrompt(tag?.product_matching_prompt || "");
    setCategoryIds(Array.isArray(tag?.categories) ? tag.categories : []);
  }, [isOpen, tag]);

  const handleSave = async () => {
    const trimmedId = id.trim().toUpperCase();
    if (!trimmedId) {
      dispatch(addToast({ message: "Tag ID is required", type: "error" }));
      return;
    }

    if (isPublic && !displayValue.trim()) {
      dispatch(addToast({ message: "Display value is required for public tags", type: "error" }));
      return;
    }

    const display =
      displayValue.trim() || ordinal !== "" || color
        ? {
            value: displayValue.trim(),
            ...(ordinal !== "" ? { ordinal: Number(ordinal) } : {}),
            ...(color ? { color } : {}),
          }
        : undefined;

    const productMatchingPrompt = matchingPrompt.trim();
    const promptChanged =
      !isEditMode || productMatchingPrompt !== originalPrompt;

    try {
      setSaving(true);
      if (isEditMode) {
        await updateProductTag(trimmedId, {
          public: isPublic,
          display,
          product_matching_prompt: productMatchingPrompt,
          categories: categoryIds,
        });
      } else {
        await createProductTag({
          id: trimmedId,
          public: isPublic,
          display,
          product_matching_prompt: productMatchingPrompt,
          categories: categoryIds,
        });
      }
      dispatch(addToast({
        message: isEditMode ? "Product tag updated" : "Product tag created",
        type: "success",
        timer: 4,
      }));
      onSave?.();
      setIsOpen(false);

      if (productMatchingPrompt && promptChanged && onRequestScan) {
        onRequestScan({
          tagId: trimmedId,
          reverify: isEditMode && Boolean(originalPrompt),
        });
      }
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to save product tag",
        type: "error",
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      title={`${isEditMode ? "Edit" : "Create"} Product Tag`}
      width="lg"
    >
      <div className="px-5 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="tag-id">
            Tag ID <span className="text-rose-500">*</span>
          </label>
          <input
            id="tag-id"
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value.toUpperCase())}
            disabled={isEditMode}
            className="form-input w-full uppercase disabled:opacity-60 disabled:cursor-not-allowed"
            placeholder="e.g. ORGANIC"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            The unique identifier for this tag. Automatically uppercased and cannot be changed later.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="tag-matching-prompt">
            Product Matching Prompt
          </label>
          <textarea
            id="tag-matching-prompt"
            value={matchingPrompt}
            onChange={(e) => setMatchingPrompt(e.target.value)}
            rows={4}
            className="form-textarea w-full"
            placeholder="e.g. Tag products that are certified organic or explicitly labeled as organic on the packaging."
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Used by the LLM to automatically tag products. If left empty, products will not be auto-tagged with this tag.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Applicable Categories</label>
          <CategoryMultiSelect
            options={categoryOptions}
            value={categoryIds}
            onChange={setCategoryIds}
            loading={categoriesLoading}
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Limit this tag to specific categories. If none are selected, the tag applies to all categories.
          </p>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={isPublic}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsPublic(checked);
                if (!checked) {
                  setDisplayValue("");
                  setOrdinal("");
                  setColor(DEFAULT_COLOR);
                }
              }}
            />
            <span className="text-sm ml-2">Public</span>
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Public tags are visible to shoppers on the storefront.
          </p>
        </div>

        {isPublic && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="tag-display-value">
                Display Value <span className="text-rose-500">*</span>
              </label>
              <input
                id="tag-display-value"
                type="text"
                value={displayValue}
                onChange={(e) => setDisplayValue(e.target.value)}
                className="form-input w-full"
                placeholder="e.g. Organic"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                The human-friendly label shown to shoppers.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="tag-ordinal">
                Display Ordinal
              </label>
              <input
                id="tag-ordinal"
                type="number"
                value={ordinal}
                onChange={(e) => setOrdinal(e.target.value)}
                className="form-input w-full md:w-40"
                placeholder="0"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Controls the sort order when displaying tags.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tag Color</label>
              <div className="flex items-center flex-wrap gap-2">
                {PRESET_COLORS.map((preset) => {
                  const selected = color?.toLowerCase() === preset.toLowerCase();
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setColor(preset)}
                      title={preset}
                      className={`w-7 h-7 rounded-full border transition-transform hover:scale-110 ${
                        selected
                          ? "ring-2 ring-offset-2 ring-indigo-500 dark:ring-offset-slate-800 border-transparent"
                          : "border-slate-300 dark:border-slate-600"
                      }`}
                      style={{ backgroundColor: preset }}
                    />
                  );
                })}
                <label
                  className="w-7 h-7 rounded-full border border-dashed border-slate-400 dark:border-slate-500 flex items-center justify-center cursor-pointer overflow-hidden relative"
                  title="Custom color"
                >
                  <input
                    type="color"
                    value={color || DEFAULT_COLOR}
                    onChange={(e) => setColor(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                  </svg>
                </label>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span
                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
                  style={{ backgroundColor: color, color: "#fff" }}
                >
                  {displayValue.trim() || id.trim() || "Preview"}
                </span>
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="form-input w-32 font-mono text-sm"
                  placeholder="#6366f1"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Color used to display this tag on the storefront.
              </p>
            </div>
          </>
        )}
      </div>

      <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
        <SecondaryButton onClick={() => setIsOpen(false)} disabled={saving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSave} loading={saving} disabled={saving}>
          {isEditMode ? "Save" : "Create"}
        </PrimaryButton>
      </div>
    </ModalBasic>
  );
}

export function TagScanConfirmModal({
  open,
  tagId,
  reverify,
  onClose,
  onQueued,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [enqueueing, setEnqueueing] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !tagId) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    previewProductTagScan(tagId, { reverify })
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((error) => {
        if (!cancelled) {
          dispatch(addToast({
            message: error.response?.data?.message || "Failed to load scan preview",
            type: "error",
          }));
          onClose?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when open/tag/reverify change
  }, [open, tagId, reverify, dispatch]);

  const handleConfirm = async () => {
    try {
      setEnqueueing(true);
      const job = await enqueueProductTagScan(tagId, { reverify });
      dispatch(addToast({
        message: `Queued scan of ${preview?.candidate_count ?? "?"} products for ${tagId}`,
        type: "success",
        timer: 5,
      }));
      onQueued?.(job);
      onClose?.();
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to queue tag scan",
        type: "error",
      }));
    } finally {
      setEnqueueing(false);
    }
  };

  const count = preview?.candidate_count;
  const body = loading
    ? "Counting matching products…"
    : reverify
      ? `Re-scan ~${count ?? "?"} products for tag ${tagId}? Products that no longer match will have the tag removed.`
      : `Scan ~${count ?? "?"} products for tag ${tagId}? Matching products will get this tag added.`;

  return (
    <ModalBasic
      id="product-tag-scan-confirm"
      title="Scan products for tag"
      modalOpen={open}
      setModalOpen={(v) => {
        if (!v) onClose?.();
      }}
      width="md"
    >
      <div className="px-5 py-4 space-y-6">
        <p className="text-sm text-slate-600 dark:text-slate-300">{body}</p>
        <div className="flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700 pt-4">
          <SecondaryButton onClick={onClose} disabled={enqueueing}>
            Skip
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            loading={enqueueing || loading}
            disabled={enqueueing || loading || count === 0}
          >
            {count === 0 ? "No products to scan" : "Start scan"}
          </PrimaryButton>
        </div>
      </div>
    </ModalBasic>
  );
}

export default ProductTagModal;
